import React from "react";
import { PartyStore } from "@store/PartyStore.js";
import { getCombatManager } from "../../engine/combat/combatManagerRef.js";

/**
 * Combat-feed line highlighter. Plain feed strings stay the engine
 * contract; color is applied at render time by matching known names and
 * number phrases, so no `pushFeed` call site changes:
 *
 * - party member names   → ATB blue
 * - registered enemy names → hostile red
 * - "for N <type> damage" → amber; "(CRITICAL!)" → bright red
 * - "+N HP" / "N HP" heal phrasing → green
 */

const PARTY_COLOR = "#7dcfff";
const ENEMY_COLOR = "#ff8f8f";
const DAMAGE_COLOR = "#ffb45c";
const HEAL_COLOR = "#7dff9d";
const CRIT_COLOR = "#ff5555";

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

interface Rule {
  readonly pattern: string;
  readonly color: string;
  readonly bold?: boolean;
}

/** Build the highlight rule set from live party + map enemy names. */
function buildRules(): readonly Rule[] {
  const rules: Rule[] = [
    { pattern: "\\(CRITICAL!\\)", color: CRIT_COLOR, bold: true },
    { pattern: "\\+\\d+ HP", color: HEAL_COLOR, bold: true },
    { pattern: "\\d+ [a-z]+ damage", color: DAMAGE_COLOR, bold: true },
  ];
  // Longest names first so "Cerberus K9 Alpha" wins over "Cerberus K9".
  const party = PartyStore.getMembers()
    .map((m) => m.name)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);
  for (const name of party) rules.push({ pattern: escapeRegex(name), color: PARTY_COLOR });
  const enemies = (getCombatManager()?.listEnemyNames() ?? [])
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);
  for (const name of enemies) rules.push({ pattern: escapeRegex(name), color: ENEMY_COLOR });
  return rules;
}

let cachedRegex: RegExp | null = null;
let cachedRules: readonly Rule[] = [];
let cacheStamp = 0;

/** Rules change rarely (map load, party change) — rebuild at most 1/s. */
function highlightRegex(): { regex: RegExp; rules: readonly Rule[] } | null {
  const now = Date.now();
  if (!cachedRegex || now - cacheStamp > 1000) {
    cachedRules = buildRules();
    cacheStamp = now;
    cachedRegex =
      cachedRules.length > 0
        ? new RegExp(cachedRules.map((r) => `(${r.pattern})`).join("|"), "g")
        : null;
  }
  return cachedRegex ? { regex: cachedRegex, rules: cachedRules } : null;
}

/** Render one feed line as colorized spans. */
export function highlightFeedLine(message: string): React.ReactNode {
  const h = highlightRegex();
  if (!h) return message;
  const out: React.ReactNode[] = [];
  let last = 0;
  let key = 0;
  h.regex.lastIndex = 0;
  for (let m = h.regex.exec(message); m !== null; m = h.regex.exec(message)) {
    if (m.index > last) out.push(message.slice(last, m.index));
    const groupIdx = m.slice(1).findIndex((g) => g !== undefined);
    const rule = h.rules[groupIdx];
    out.push(
      <span
        key={key++}
        style={{ color: rule?.color, fontWeight: rule?.bold ? 700 : undefined }}
      >
        {m[0]}
      </span>,
    );
    last = m.index + m[0].length;
  }
  if (out.length === 0) return message;
  if (last < message.length) out.push(message.slice(last));
  return out;
}
